/**
 * Bulk CBS Cognara sweep — one-click "Analyse all critical rows".
 * Pre-screens CBS rows by heuristic severity on the server, runs the row-level
 * Cognara analyst with bounded concurrency, then renders a portfolio heatmap
 * (by cost category) plus a ranked drill-down drawer.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Loader2, Sparkles, X, ChevronRight, Flame, ShieldAlert, Gauge } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '@/lib/api';

const SEV_COLOR = { CRITICAL: '#DC2626', HIGH: '#F59E0B', MEDIUM: '#3B82F6', LOW: '#10B981' };
const SEV_ORDER = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'];

function fmtCr(v) {
  const n = Number(v || 0);
  if (!n) return '0';
  if (Math.abs(n) >= 1e7) return `${(n / 1e7).toFixed(2)} Cr`;
  if (Math.abs(n) >= 1e5) return `${(n / 1e5).toFixed(1)} L`;
  return n.toLocaleString('en-IN', { maximumFractionDigits: 0 });
}

function SevChip({ sev, count }) {
  return (
    <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold" data-testid={`bulk-sev-chip-${sev}`}
      style={{ backgroundColor: `${SEV_COLOR[sev]}1A`, color: SEV_COLOR[sev], border: `1px solid ${SEV_COLOR[sev]}40` }}>
      {sev} {count}
    </span>
  );
}

function HeatCell({ cell, onClick }) {
  const heat = cell.critical * 3 + cell.high;
  const tone = cell.critical ? SEV_COLOR.CRITICAL : cell.high ? SEV_COLOR.HIGH : SEV_COLOR.MEDIUM;
  const intensity = Math.min(0.08 + heat * 0.06, 0.4);
  return (
    <button type="button" onClick={onClick} data-testid={`bulk-heat-${cell.costCategory}`}
      className="text-left rounded-lg p-3 transition-transform hover:-translate-y-0.5"
      style={{ backgroundColor: `${tone}${Math.round(intensity * 255).toString(16).padStart(2, '0')}`, border: `1px solid ${tone}55` }}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold truncate" style={{ color: 'var(--text-primary)' }}>{cell.costCategory}</span>
        <span className="text-[10px] font-bold" style={{ color: tone }}>{cell.rows} rows</span>
      </div>
      <div className="mt-2 flex items-center gap-2 text-[10px]" style={{ color: 'var(--text-secondary)' }}>
        <span>SPI {cell.worstSpi?.toFixed?.(2) ?? '-'}</span>
        <span>CPI {cell.worstCpi?.toFixed?.(2) ?? '-'}</span>
      </div>
      <div className="mt-1 flex items-center gap-3 text-[10px]" style={{ color: 'var(--text-muted)' }}>
        <span>BAC {fmtCr(cell.bac)}</span>
        <span style={{ color: cell.cv < 0 ? SEV_COLOR.CRITICAL : SEV_COLOR.LOW }}>CV {fmtCr(cell.cv)}</span>
      </div>
      <div className="mt-2 flex gap-1">
        {SEV_ORDER.map((s) => (cell[s.toLowerCase()] ? (
          <span key={s} className="text-[9px] px-1.5 rounded" style={{ backgroundColor: `${SEV_COLOR[s]}26`, color: SEV_COLOR[s] }}>
            {cell[s.toLowerCase()]}
          </span>
        ) : null))}
      </div>
    </button>
  );
}

function RowDrawer({ row, onClose }) {
  if (!row) return null;
  const recs = row.recommendations || {};
  const Section = ({ title, items }) => (!items?.length ? null : (
    <div className="mb-4">
      <div className="text-[10px] uppercase tracking-wider mb-1.5" style={{ color: 'var(--text-muted)' }}>{title}</div>
      <ul className="space-y-1">
        {items.map((t, i) => (
          <li key={i} className="text-xs flex gap-2" style={{ color: 'var(--text-secondary)' }}>
            <span style={{ color: SEV_COLOR[row.severity] }}>▪</span><span>{t}</span>
          </li>
        ))}
      </ul>
    </div>
  ));
  return createPortal(
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[9998] flex justify-end" style={{ backgroundColor: 'var(--overlay-bg)' }} onClick={onClose}>
      <motion.aside initial={{ x: 480 }} animate={{ x: 0 }} exit={{ x: 480 }} transition={{ type: 'spring', damping: 26, stiffness: 240 }}
        onClick={(e) => e.stopPropagation()} data-testid="bulk-cognara-drawer"
        className="w-full max-w-[520px] overflow-y-auto" style={{ height: '100dvh', backgroundColor: 'var(--card-bg)', borderLeft: '1px solid var(--border-color)' }}>
        <div className="sticky top-0 px-5 py-4 flex items-start gap-3" style={{ backgroundColor: 'var(--card-bg)', borderBottom: '1px solid var(--border-color)' }}>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[10px] px-2 py-0.5 rounded-full font-bold"
                style={{ backgroundColor: `${SEV_COLOR[row.severity]}1A`, color: SEV_COLOR[row.severity] }}>{row.severity}</span>
              <span className="text-xs font-mono" style={{ color: 'var(--text-muted)' }}>{row.costCode}</span>
            </div>
            <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{row.costCodeName || row.costCode}</h3>
          </div>
          <button type="button" onClick={onClose} data-testid="bulk-cognara-drawer-close">
            <X className="w-4 h-4" style={{ color: 'var(--text-muted)' }} />
          </button>
        </div>
        <div className="px-5 py-4">
          <p className="text-sm font-medium mb-4" style={{ color: 'var(--text-primary)' }}>{row.headline}</p>
          <div className="grid grid-cols-4 gap-2 mb-5">
            {[['SPI', row.spi?.toFixed?.(2)], ['CPI', row.cpi?.toFixed?.(2)], ['CV', fmtCr(row.cv)], ['VAC', fmtCr(row.vac)]].map(([k, v]) => (
              <div key={k} className="rounded-lg p-2 text-center" style={{ backgroundColor: 'var(--surface-bg)', border: '1px solid var(--border-subtle)' }}>
                <div className="text-[9px]" style={{ color: 'var(--text-muted)' }}>{k}</div>
                <div className="text-xs font-bold" style={{ color: 'var(--text-primary)' }}>{v ?? '-'}</div>
              </div>
            ))}
          </div>
          {[['Schedule', row.schedule_analysis], ['Cost', row.cost_analysis], ['EAC view', row.eac_view], ['Delay impact', row.delay_impact]].map(([k, v]) => (
            <div key={k} className="mb-3">
              <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color: 'var(--text-muted)' }}>{k}</div>
              <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{v}</p>
            </div>
          ))}
          <Section title="Overrun reasons" items={row.overrun_reasons} />
          <Section title="Emerging variances" items={row.emerging_variances} />
          <Section title="Schedule actions" items={recs.schedule} />
          <Section title="Resource actions" items={recs.resource} />
          <Section title="Cost actions" items={recs.cost} />
        </div>
      </motion.aside>
    </motion.div>,
    document.body,
  );
}

export default function BulkCbsCognaraPanel({ projectId, rows }) {
  const [minSeverity, setMinSeverity] = useState('HIGH');
  const [busy, setBusy] = useState(false);
  const [sweep, setSweep] = useState(null);
  const [openRow, setOpenRow] = useState(null);
  const [catFilter, setCatFilter] = useState(null);

  useEffect(() => {
    if (!projectId) return;
    setSweep(null); setCatFilter(null);
    api.get(`/v1/projects/${projectId}/evm/cbs/bulk-cognara-analysis/latest`)
      .then((r) => { if (r.data?.rows?.length) setSweep(r.data); })
      .catch(() => {});
  }, [projectId]);

  const run = useCallback(async () => {
    if (!projectId || !rows?.length) { toast.error('No CBS rows loaded yet'); return; }
    setBusy(true);
    const tid = toast.loading('Cognara is sweeping critical CBS rows…');
    try {
      const r = await api.post(`/v1/projects/${projectId}/evm/cbs/bulk-cognara-analysis`, {
        rows, minSeverity, maxRows: 20, leavesOnly: true,
      });
      setSweep(r.data);
      setCatFilter(null);
      toast.success(
        `${r.data.analysedRows} of ${r.data.screenedRows} rows analysed · ${r.data.severityCounts?.CRITICAL || 0} critical`,
        { id: tid },
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk Cognara sweep failed', { id: tid });
    } finally { setBusy(false); }
  }, [projectId, rows, minSeverity]);

  const visibleRows = useMemo(() => {
    const list = sweep?.rows || [];
    return catFilter ? list.filter((r) => (r.costCategory || 'UNCATEGORISED') === catFilter) : list;
  }, [sweep, catFilter]);

  return (
    <div className="rounded-xl p-5" data-testid="bulk-cbs-cognara-panel"
      style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
      <div className="flex items-center gap-3 flex-wrap mb-4">
        <div className="p-2 rounded-lg" style={{ background: '#8B5CF612', border: '1px solid #8B5CF620' }}>
          <Flame className="w-4 h-4" style={{ color: '#8B5CF6' }} />
        </div>
        <div className="flex-1 min-w-[200px]">
          <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Portfolio Cognara Sweep</h3>
          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
            Analyse every CBS row at or above the chosen severity in one click — ranked worst-first with a category heatmap
          </p>
        </div>
        <select value={minSeverity} onChange={(e) => setMinSeverity(e.target.value)} data-testid="bulk-cognara-severity"
          className="rounded-lg px-3 py-2 text-xs"
          style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}>
          <option value="CRITICAL">Critical only</option>
          <option value="HIGH">High and above</option>
          <option value="MEDIUM">Medium and above</option>
        </select>
        <Button size="sm" onClick={run} disabled={busy} data-testid="bulk-cognara-run-btn"
          className="bg-[#8B5CF6] hover:bg-[#7C3AED] text-white">
          {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
          Analyse all critical rows
        </Button>
      </div>

      {!sweep ? (
        <div className="py-8 text-center" data-testid="bulk-cognara-empty">
          <ShieldAlert className="w-8 h-8 mx-auto mb-2" style={{ color: 'var(--text-disabled)' }} />
          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
            No sweep yet. {rows?.length || 0} CBS rows ready to screen.
          </p>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 flex-wrap mb-4" data-testid="bulk-cognara-summary">
            {SEV_ORDER.map((s) => (sweep.severityCounts?.[s] ? <SevChip key={s} sev={s} count={sweep.severityCounts[s]} /> : null))}
            <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
              {sweep.analysedRows} analysed of {sweep.screenedRows} screened ({sweep.totalRows} total)
              {sweep.truncated ? ' · capped at 20 worst rows' : ''}
            </span>
            <span className="text-[11px] ml-auto" style={{ color: 'var(--text-disabled)' }}>
              {sweep.generatedAt ? `saved · ${new Date(sweep.generatedAt).toLocaleString()}` : ''}
            </span>
          </div>

          {!!sweep.heatmap?.length && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 mb-5" data-testid="bulk-cognara-heatmap">
              {sweep.heatmap.map((cell) => (
                <HeatCell key={cell.costCategory} cell={cell}
                  onClick={() => setCatFilter(catFilter === cell.costCategory ? null : cell.costCategory)} />
              ))}
            </div>
          )}

          {catFilter && (
            <button type="button" onClick={() => setCatFilter(null)} data-testid="bulk-cognara-clear-filter"
              className="text-[11px] mb-3 px-2 py-1 rounded-full"
              style={{ backgroundColor: 'var(--active-bg)', color: 'var(--accent-text)' }}>
              Filtered: {catFilter} · clear
            </button>
          )}

          <div className="rounded-lg overflow-hidden" style={{ border: '1px solid var(--border-subtle)' }} data-testid="bulk-cognara-ranked-list">
            {visibleRows.map((r) => (
              <button type="button" key={r.costCode} onClick={() => setOpenRow(r)} data-testid={`bulk-cognara-row-${r.costCode}`}
                className="w-full text-left px-3 py-2.5 flex items-center gap-3 transition-colors hover:bg-[var(--hover-bg)]"
                style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                <span className="w-1 self-stretch rounded" style={{ backgroundColor: SEV_COLOR[r.severity] }} />
                <span className="text-[10px] px-1.5 py-0.5 rounded font-bold shrink-0"
                  style={{ backgroundColor: `${SEV_COLOR[r.severity]}1A`, color: SEV_COLOR[r.severity] }}>{r.severity}</span>
                <span className="text-xs font-mono shrink-0" style={{ color: 'var(--text-muted)' }}>{r.costCode}</span>
                <span className="text-xs flex-1 truncate" style={{ color: 'var(--text-primary)' }}>{r.headline}</span>
                <span className="text-[11px] font-mono shrink-0 flex items-center gap-1" style={{ color: 'var(--text-secondary)' }}>
                  <Gauge className="w-3 h-3" />{r.spi?.toFixed?.(2)}/{r.cpi?.toFixed?.(2)}
                </span>
                <ChevronRight className="w-3.5 h-3.5 shrink-0" style={{ color: 'var(--text-muted)' }} />
              </button>
            ))}
            {!visibleRows.length && (
              <div className="px-3 py-6 text-center text-xs" style={{ color: 'var(--text-muted)' }}>No rows in this category</div>
            )}
          </div>
        </>
      )}

      <AnimatePresence>
        {openRow && <RowDrawer row={openRow} onClose={() => setOpenRow(null)} />}
      </AnimatePresence>
    </div>
  );
}
